/**
 * The King.Prostor Web views (2026-09-28, Teo: "1 group is 1 page, with listed
 * entries for each layer … same ui/ux for web as for cad page") — the twins of
 * the KingCAD views in renderCad.ts:
 *
 * - the overview (`…/docs/web`): the page-list nav on the left and, in the
 *   article, one line (»14 tem · 75 slojev«) and a card grid — one card per
 *   theme page with its entry count and the first entry names. No sections,
 *   no pills, no rail (Teo, same day: the sidebar repeated three times).
 *
 * - a theme page (`…/docs/web/<slug>`): one page per theme — every entry as a
 *   section (`#sloj-<key>`, renderSection.ts). The rail lists the entries
 *   (children indented); the pager walks the pages in theme order.
 */

import type { WikiEntry } from '../../../shared/types/layerWiki.js';
import { docsCopy } from '../docsCopy.js';
import type { EditState } from '../docsEdit.js';
import { esc } from '../docsHtml.js';
import {
  navLabel,
  webLandingCards,
  webPageNeighbours,
  webPagePath,
  type DocsCatalogue,
  type WebPage,
} from '../docsModel.js';
import { docsPath, webEntryAnchorId } from '../docsRouter.js';
import { renderCards } from './renderCards.js';
import { renderWebNav } from './renderKatalog.js';
import { renderEntrySection, renderPager } from './renderSection.js';

export interface WebState {
  query: string;
  navOpen: boolean;
}

export interface WebPageState extends WebState {
  /** King-admin editing (0569); absent or `isKingAdmin: false` renders the public page. */
  edit?: EditState;
}

// ---------------------------------------------------------------------------
// Overview (card landing)
// ---------------------------------------------------------------------------

export function renderWeb(cat: DocsCatalogue, state: WebState): string {
  const c = docsCopy.web;
  const article = `
    <article class="docs-article docs-article--landing">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(docsCopy.katalog.crumbRoot)}</a>
        <span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(c.crumb)}</span>
      </div>
      <h1 class="docs-entry-title" tabindex="-1">${esc(c.title)}</h1>
      <p class="docs-landing-intro">${esc(c.intro(cat.counts.themes, cat.counts.layers))}</p>
      ${renderCards(webLandingCards(cat))}
    </article>`;
  return `
    <div class="docs-katalog docs-katalog--tree">
      ${renderWebNav(cat, state, null)}
      <div class="docs-katalog-body docs-katalog-body--wide">
        ${article}
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Theme page
// ---------------------------------------------------------------------------

function renderWebPageArticle(
  cat: DocsCatalogue,
  page: WebPage,
  edit: EditState | undefined
): string {
  const c = docsCopy;
  const crumbs = [
    `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.katalog.crumbRoot)}</a>`,
    `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'web' }))}" data-docs-link>${esc(c.web.crumb)}</a>`,
  ];
  const { prev, next } = webPageNeighbours(cat, page.slug);
  const end = (p: WebPage | null): { href: string; label: string } | null =>
    p ? { href: webPagePath(p), label: p.theme } : null;
  return `
    <article class="docs-article docs-cad-page">
      <div class="docs-crumbs">${crumbs.join('<span aria-hidden="true">/</span>')}</div>
      <h1 class="docs-entry-title" tabindex="-1">${esc(page.theme)}</h1>
      <div class="docs-cad-meta">${esc(c.cad.layersIn(page.entries.length))}</div>
      ${page.entries.map((e) => renderEntrySection(cat, e, { id: webEntryAnchorId(e.key), section: 'web', ...(edit ? { edit } : {}) })).join('')}
      ${renderPager(end(prev), end(next))}
    </article>`;
}

function renderWebPageAside(page: WebPage): string {
  const c = docsCopy;
  const items = page.entries.map((e: WikiEntry) => {
    // A child of an entry on THIS page indents under it; a child whose parent sits elsewhere stays level.
    const child = e.parentKey !== null && page.entries.some((p) => p.key === e.parentKey);
    return `<a${child ? ' class="docs-rail-child"' : ''} href="#${esc(webEntryAnchorId(e.key))}">${esc(navLabel(e))}</a>`;
  });
  return `
    <aside class="docs-aside" aria-label="${esc(c.web.page.onThisPage)}">
      <div class="docs-aside-title">${esc(c.web.page.onThisPage)}</div>
      <div class="docs-rail">${items.join('')}</div>
      <div class="docs-permalink">${esc(c.katalog.permalink)}<br><code>${esc(webPagePath(page))}</code></div>
    </aside>`;
}

function renderWebPageNotFound(): string {
  const c = docsCopy;
  return `
    <article class="docs-article">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.katalog.crumbRoot)}</a>
        <span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(c.web.crumb)}</span>
      </div>
      <h1 class="docs-entry-title docs-entry-title--notice" tabindex="-1">${esc(c.web.page.notFound)}</h1>
      <p class="docs-desc"><a href="${esc(docsPath({ view: 'web' }))}" data-docs-link>${esc(c.web.page.notFoundLink)}</a></p>
    </article>`;
}

/** A theme page; `page` null renders the notice with the page-list nav beside it. */
export function renderWebPage(
  cat: DocsCatalogue,
  page: WebPage | null,
  state: WebPageState
): string {
  return `
    <div class="docs-katalog docs-katalog--tree">
      ${renderWebNav(cat, state, page?.slug ?? null)}
      <div class="docs-katalog-body">
        ${page ? renderWebPageArticle(cat, page, state.edit) : renderWebPageNotFound()}
        ${page ? renderWebPageAside(page) : '<span></span>'}
      </div>
    </div>`;
}
